import { isBefore } from 'date-fns';
import { useState } from 'react';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import {
    PutServiceOrderCompletionProps,
    PutServiceOrderStartInfoProps,
    usePostServiceOrderCompletionsMutation,
    usePostServiceOrderStartInfosMutation,
} from '../../../api/serviceOrdersApi';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { DateInput, isDateStringEmpty, isValidDate } from '../../../sharedComponents/dateHelper';
import { AccessoryType, CollectedAccessories } from '../accessory.types';
import { ServiceOrder, ServiceOrderStatus } from '../serviceOrder.types';
import { getObjectKeyForAccessory } from '../serviceOrderUtils';
import { trimmedOrUndefined } from '../../../api/utils';

export type ServiceOrderSidebarExecutionDetails = {
    accessories: CollectedAccessories;
    generalInformation?: string;
    startedAt: DateInput;
    completedAt: DateInput;
};

export type SidebarFormFieldError<T> = { type: T };
export type ExecutionDateErrorType =
    'invalidDate' | 'dateInFuture' | 'endDateBeforeStartDateError' | 'removedDate' | 'oneMandatory';

type AccessoryTypeErrorType = 'invalidValue';
export type ServiceOrderExecutionFormErrors = {
    [key in AccessoryType]?: SidebarFormFieldError<AccessoryTypeErrorType>;
} & {
    startedAt?: SidebarFormFieldError<ExecutionDateErrorType>;
    completedAt?: SidebarFormFieldError<ExecutionDateErrorType>;
};

export const useServiceOrderExecutionForm = (
    selectedServiceOrder: ServiceOrder | undefined,
    closeSidebar: () => void,
) => {
    const [updateStartInfos, { isLoading: isStartInfoLoading }] = usePostServiceOrderStartInfosMutation();
    const [updateExecutionDetails, { isLoading: isCompletionLoading }] = usePostServiceOrderCompletionsMutation();

    const [formErrors, setFormErrors] = useState<ServiceOrderExecutionFormErrors>({});
    const [executionDetails, setExecutionDetails] = useState<ServiceOrderSidebarExecutionDetails>({
        accessories: selectedServiceOrder?.vehicle.collectedAccessories ?? {},
        generalInformation: selectedServiceOrder?.generalInformation,
        startedAt: selectedServiceOrder?.startedAt,
        completedAt: selectedServiceOrder?.completedAt,
    });

    const validateForm = (): boolean => {
        if (selectedServiceOrder === undefined) {
            return false;
        }

        const newFormErrors: ServiceOrderExecutionFormErrors = {};

        const withoutCompletionDate =
            isDateStringEmpty(executionDetails.completedAt) && selectedServiceOrder?.completedAt === undefined;

        const allAccessoriesAreValid =
            withoutCompletionDate || validateAccessoriesRequired(executionDetails, selectedServiceOrder, newFormErrors);

        newFormErrors.startedAt = validateStartedAt(executionDetails, selectedServiceOrder);
        newFormErrors.completedAt = validateCompletedAt(executionDetails, selectedServiceOrder.status);

        setFormErrors(newFormErrors);
        return (
            allAccessoriesAreValid && newFormErrors.startedAt === undefined && newFormErrors.completedAt === undefined
        );
    };

    const handleStartInfoUpdate = runInBackgroundCallback(async () => {
        if (validateForm() && selectedServiceOrder !== undefined) {
            try {
                const executionDetailsPayload = getStartInfoPayload(selectedServiceOrder, executionDetails);
                await updateStartInfos(executionDetailsPayload).unwrap();
                showSuccessNotification('outboundPortal.notification.serviceOrder.start.info');
                closeSidebar();
            } catch (error) {
                handleQueryError(error);
            }
        }
    });

    const handleCompletionUpdate = runInBackgroundCallback(async () => {
        if (validateForm() && selectedServiceOrder !== undefined) {
            try {
                const executionDetailsPayload = getCompletionPayload(selectedServiceOrder, executionDetails);
                await updateExecutionDetails(executionDetailsPayload).unwrap();
                showSuccessNotification('outboundPortal.notification.serviceOrder.completion.success');
                closeSidebar();
            } catch (error) {
                handleQueryError(error);
            }
        }
    });

    return {
        formErrors,
        executionDetails,
        setExecutionDetails,
        handleSaveServiceOrder: isDateStringEmpty(executionDetails.completedAt)
            ? handleStartInfoUpdate
            : handleCompletionUpdate,
        isExecutionDetailsLoading: isCompletionLoading || isStartInfoLoading,
    };
};

const validateStartedAt = (
    { startedAt: startedAtFormValue }: ServiceOrderSidebarExecutionDetails,
    serviceOrder: ServiceOrder,
): SidebarFormFieldError<ExecutionDateErrorType> | undefined => {
    if (!isDateStringEmpty(startedAtFormValue) && !isValidDate(startedAtFormValue)) {
        return { type: 'invalidDate' };
    }

    if (isDateStringEmpty(startedAtFormValue) && serviceOrder.startedAt !== undefined) {
        return { type: 'removedDate' };
    }

    return undefined;
};

const validateCompletedAt = (
    { startedAt: startedAtFormValue, completedAt: completedAtFormValue }: ServiceOrderSidebarExecutionDetails,
    serviceOrderStatus: ServiceOrderStatus,
): SidebarFormFieldError<ExecutionDateErrorType> | undefined => {
    const startedAtIsEmpty = isDateStringEmpty(startedAtFormValue);
    const completedAtIsEmpty = isDateStringEmpty(completedAtFormValue);

    if (serviceOrderStatus === ServiceOrderStatus.COMPLETED && completedAtIsEmpty) {
        return { type: 'removedDate' };
    }

    if (startedAtIsEmpty && completedAtIsEmpty) {
        return { type: 'oneMandatory' };
    }

    if (!completedAtIsEmpty && !isValidDate(completedAtFormValue)) {
        return { type: 'invalidDate' };
    }

    if (completedAtFormValue === undefined) {
        return undefined;
    }

    if (completedAtFormValue > new Date()) {
        return { type: 'dateInFuture' };
    }

    if (isValidDate(startedAtFormValue) && isBefore(completedAtFormValue, startedAtFormValue)) {
        return { type: 'endDateBeforeStartDateError' };
    }

    return undefined;
};

export const validateAccessoriesRequired = (
    executionDetails: ServiceOrderSidebarExecutionDetails,
    selectedServiceOrder: ServiceOrder,
    newFormErrors: ServiceOrderExecutionFormErrors,
) => {
    let allAccessoriesAreValid = true;
    selectedServiceOrder.vehicle.requestedAccessories.forEach((requestedAccessory: AccessoryType) => {
        const accessoryValue = executionDetails.accessories[getObjectKeyForAccessory(requestedAccessory)];
        if (accessoryValue === undefined || accessoryValue < 0) {
            newFormErrors[requestedAccessory] = { type: 'invalidValue' };
            allAccessoriesAreValid = false;
        }
    });

    return allAccessoriesAreValid;
};

const getStartInfoPayload = (
    selectedServiceOrder: ServiceOrder,
    executionDetails: ServiceOrderSidebarExecutionDetails,
): PutServiceOrderStartInfoProps => ({
    serviceOrderId: selectedServiceOrder.id,
    startInfo: {
        type: selectedServiceOrder.type,
        vehicleId: selectedServiceOrder.vehicle.id,
        startedAt: executionDetails.startedAt as Date,
        generalInformation: trimmedOrUndefined(executionDetails.generalInformation),
        collectedAccessories: executionDetails.accessories,
    },
});

const getCompletionPayload = (
    selectedServiceOrder: ServiceOrder,
    executionDetails: ServiceOrderSidebarExecutionDetails,
): PutServiceOrderCompletionProps => ({
    serviceOrderId: selectedServiceOrder.id,
    completion: {
        type: selectedServiceOrder.type,
        vehicleId: selectedServiceOrder.vehicle.id,
        startedAt: executionDetails.startedAt ? (executionDetails.startedAt as Date) : undefined,
        completedAt: executionDetails.completedAt as Date,
        generalInformation: trimmedOrUndefined(executionDetails.generalInformation),
        collectedAccessories: executionDetails.accessories,
    },
});
